import { useModuleAccess } from "@/hooks/useModuleAccess";
import { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { driver, type DriveStep } from "driver.js";
import "driver.js/dist/driver.css";
import "./tour-theme.css";
import { useAuth } from "@/contexts/AuthContext";
import { useUiPref } from "@/hooks/useUiPref";
import { getMobileTourSteps } from "@/lib/tour/steps";
import { TourWelcome, TourFinish } from "./TourOverlays";

/** Event to (re)launch the mobile tour, e.g. from Mobile Settings. */
export const START_MOBILE_TOUR_EVENT = "besthr:start-mobile-tour";

type DriverInstance = ReturnType<typeof driver>;

/**
 * Mobile product tour controller, mounted in MobileLayout. Shows an animated
 * welcome screen, runs a single-screen driver.js walkthrough of the bottom
 * navigation, then a finish screen. Auto-starts once per user on the mobile
 * home (tracked in user_ui_prefs) and supports manual replay.
 */
export function MobileProductTour() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const { canAccess, ready } = useModuleAccess();
  const location = useLocation();

  const [done, setDone] = useUiPref("tour_done_mobile", false, { enabled: !!user });
  const autoStarted = useRef(false);
  const running = useRef(false);

  const [welcomeOpen, setWelcomeOpen] = useState(false);
  const [finishOpen, setFinishOpen] = useState(false);

  const markDone = () => {
    if (!done) setDone(true);
  };

  const beginTour = () => {
    if (running.current) return;
    running.current = true;
    try { sessionStorage.removeItem("besthr_start_mobile_tour"); } catch {}
    setFinishOpen(false);
    setWelcomeOpen(true);
  };

  const skipFromWelcome = () => {
    setWelcomeOpen(false);
    running.current = false;
    markDone();
  };

  const runDriver = () => {
    setWelcomeOpen(false);

    // Only steps whose target is actually on screen.
    const steps = getMobileTourSteps(t, canAccess).filter(
      step => {
        const element = step.element ? document.querySelector(step.element) : null;
        return element && element.getClientRects().length > 0;
      },
    );
    if (steps.length === 0) {
      running.current = false;
      markDone();
      return;
    }

    const driverSteps: DriveStep[] = steps.map((st) => ({
      element: st.element,
      popover: { title: st.title, description: st.description, side: st.side, align: st.align },
    }));

    let completed = false;

    const d = driver({
      showProgress: true,
      allowClose: true,
      smoothScroll: true,
      popoverClass: "besthr-tour",
      nextBtnText: t("tour.common.next", "Next"),
      prevBtnText: t("tour.common.prev", "Back"),
      doneBtnText: t("tour.common.done", "Done"),
      progressText: t("tour.common.progress", "{{current}} of {{total}}"),
      steps: driverSteps,
      onNextClick: () => {
        if (d.hasNextStep()) d.moveNext();
        else { completed = true; d.destroy(); }
      },
      onDestroyed: () => {
        markDone();
        if (completed) setFinishOpen(true);
        else running.current = false;
      },
    });

    d.drive();
  };

  const closeFinish = () => {
    setFinishOpen(false);
    running.current = false;
  };

  // Auto-start on the mobile home for users who haven't completed/skipped it,
  // plus a forced replay via sessionStorage flag (survives navigation).
  useEffect(() => {
    if (!user || !ready) return; // not authenticated
    if (location.pathname !== "/mobile") return;
    if (done === undefined) return;

    const flagged = sessionStorage.getItem("besthr_start_mobile_tour") === "1";
    if (flagged) {
      autoStarted.current = true;
      const id = window.setTimeout(() => beginTour(), 400);
      return () => window.clearTimeout(id);
    }

    if (autoStarted.current || done) return;
    autoStarted.current = true;
    const id = window.setTimeout(() => beginTour(), 700);
    return () => window.clearTimeout(id);
  }, [location.pathname, done, ready]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const handler = () => beginTour();
    window.addEventListener(START_MOBILE_TOUR_EVENT, handler);
    return () => window.removeEventListener(START_MOBILE_TOUR_EVENT, handler);
  }, [done]);

  return (
    <>
      <TourWelcome
        open={welcomeOpen}
        title={t("tour.mobile.welcome.title")}
        description={t("tour.mobile.welcome.description")}
        startLabel={t("tour.common.start", "Start tour")}
        skipLabel={t("tour.common.skip", "Skip")}
        onStart={runDriver}
        onSkip={skipFromWelcome}
      />
      <TourFinish
        open={finishOpen}
        title={t("tour.mobile.finish.title")}
        description={t("tour.mobile.finish.description")}
        doneLabel={t("tour.common.done", "Done")}
        onDone={closeFinish}
      />
    </>
  );
}
